/** @jsxImportSource @emotion/react */
import { createTheme, Popover, type PopoverProps, ThemeProvider } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { type KeyboardEvent, useState } from 'react';

import { KAI_SHIKI_TYPOGRAPHY } from '@/Client/Styles/kaiShikiTokens';
import { EditIcon } from '@kaiizen/kai-shiki/react';

import { DEFAULT_PHOTO_DATE_EDITOR_STYLES, type PhotoDateEditorStyles } from './photoDateEditorStyles';

/* MUI's calendar, set in the styleguide's face. It is a floating surface of its own, so it takes
   none of the app theme's form-field styling. */
const CALENDAR_THEME = createTheme({ typography: { fontFamily: KAI_SHIKI_TYPOGRAPHY.fontFamily } });

/** Which side of the button the calendar opens on. */
export type PhotoDateEditorPlacement = 'below' | 'above';

/* Below hangs from the button's bottom-right, for a control at the end of a row. Above rises from
   its top-left, for a control in a bar along the bottom of the screen. */
const POPOVER_ORIGINS: Record<PhotoDateEditorPlacement, Pick<PopoverProps, 'anchorOrigin' | 'transformOrigin'>> = {
  below: { anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' } },
  above: { anchorOrigin: { vertical: 'top', horizontal: 'left' }, transformOrigin: { vertical: 'bottom', horizontal: 'left' } },
};

/**
 * The picked day at the photo's original time of day, so the edit moves the photo between days
 * without reordering it within one. A photo with no date lands at noon, safely inside the day
 * in any timezone.
 */
const onPickedDay = (day: Date, previous: number | undefined): number => {
  const time = previous == null ? new Date(0, 0, 1, 12) : new Date(previous);
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), time.getHours(), time.getMinutes(), time.getSeconds(), time.getMilliseconds()).getTime();
};

/**
 * The calendar owns the keys while it is open. Its hosts step photos on arrows and close on Escape
 * from listeners on the window, and an arrow walking the calendar's days must not also walk the
 * photo out from under it.
 */
const keepKeysInCalendar = (event: KeyboardEvent<HTMLElement>) => event.stopPropagation();

/**
 * The testing-tools control for placing a photo on any day: an edit affordance beside the date
 * that pops MUI's calendar, committing the picked day and closing.
 */
export const PhotoDateEditor = ({
  addedAt,
  onCommit,
  placement = 'below',
  styles = DEFAULT_PHOTO_DATE_EDITOR_STYLES,
}: {
  /** When the photo is currently recorded as taken, if it is. */
  addedAt?: number;
  onCommit: (addedAt: number) => void | Promise<void>;
  placement?: PhotoDateEditorPlacement;
  styles?: PhotoDateEditorStyles;
}) => {
  /* The calendar hangs off the button that opened it: the element, not a boolean, is what the
     popover anchors to. */
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <button aria-label='Edit photo date' css={styles.editButton} onClick={event => setAnchor(event.currentTarget)} type='button'>
        <EditIcon size={12} aria-hidden='true' />
      </button>
      <ThemeProvider theme={CALENDAR_THEME}>
        <Popover
          {...POPOVER_ORIGINS[placement]}
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          onKeyDown={keepKeysInCalendar}
          open={anchor != null}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateCalendar
              onChange={(day, selectionState) => {
                // Year and month picks are steps on the way to a day; only the day is the edit.
                if (!day || selectionState !== 'finish') return;
                setAnchor(null);
                void onCommit(onPickedDay(day, addedAt));
              }}
              value={addedAt == null ? null : new Date(addedAt)}
            />
          </LocalizationProvider>
        </Popover>
      </ThemeProvider>
    </>
  );
};
